import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Eye, Users, Clock, CheckCircle2, PhoneCall, BarChart3 } from "lucide-react";
import toast from "react-hot-toast";

import { getProposal, getProposalAnalytics } from "../api/prospectingReportsApi";

const TEAL = "#0f8d9b";
// eslint-disable-next-line no-restricted-syntax -- viewer-local display of a timestamp
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const Tile = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-gray-100 bg-white p-4">
    <div className="flex items-center gap-2 text-xs text-gray-500"><Icon className="w-4 h-4" style={{ color: TEAL }} /> {label}</div>
    <div className="text-2xl font-extrabold text-gray-900 mt-1">{value}</div>
  </div>
);

/** Per-proposal analytics (Proply's stats icon): views over time, unique
 * visitors, time to first view, per-section engagement and the event timeline. */
const ProposalAnalyticsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getProposal(id), getProposalAnalytics(id)])
      .then(([p, a]) => { setProposal(p.data); setData(a.data); })
      .catch(() => toast.error("Could not load analytics."))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex items-center justify-center py-24 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!data || !proposal) return <div className="p-6 text-gray-500">Analytics not available.</div>;

  const maxDay = Math.max(1, ...(data.views_by_day || []).map((d) => d.count));
  const maxSec = Math.max(1, ...(data.sections || []).map((s) => s.avg_seconds || 0));

  return (
    <div className="p-4 md:p-6">
      <button onClick={() => navigate(`/prospecting/proposals/${id}`)} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-3">
        <ArrowLeft className="w-4 h-4" /> Back to proposal
      </button>
      <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
        <BarChart3 className="w-6 h-6" style={{ color: TEAL }} /> {proposal.full_address || proposal.title}
      </h1>
      <p className="text-sm text-gray-500 mb-5">How the vendor has engaged with this proposal.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Tile icon={Eye} label="Views" value={data.views || 0} />
        <Tile icon={Users} label="Unique visitors" value={data.unique_visitors || 0} />
        <Tile icon={Clock} label="Time to first view" value={data.hours_to_first_view == null ? "—" : `${data.hours_to_first_view}h`} />
        <Tile icon={data.accepted_at ? CheckCircle2 : PhoneCall} label={data.accepted_at ? "Accepted" : "Callback"}
          value={data.accepted_at ? when(data.accepted_at) : (data.callback_requested_at ? when(data.callback_requested_at) : "—")} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-xl border border-gray-100 bg-white p-5">
          <h2 className="font-bold text-gray-900 mb-1">Views over the last 30 days</h2>
          <p className="text-xs text-gray-400 mb-4">First viewed {when(data.first_viewed_at)} · Last viewed {when(data.last_viewed_at)}</p>
          {(data.views_by_day || []).length === 0 ? (
            <p className="text-sm text-gray-400">No views yet.</p>
          ) : (
            <div className="flex items-end gap-1 h-32">
              {data.views_by_day.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end" title={`${d.date}: ${d.count}`}>
                  <div className="w-full rounded-t" style={{ height: `${Math.max(6, (d.count / maxDay) * 100)}%`, background: TEAL }} />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-gray-100 bg-white p-5">
          <h2 className="font-bold text-gray-900 mb-1">Section engagement</h2>
          <p className="text-xs text-gray-400 mb-4">Which sections the vendor viewed, and for how long on average.</p>
          {(data.sections || []).length === 0 ? (
            <p className="text-sm text-gray-400">No section data yet — it appears once the vendor scrolls the proposal.</p>
          ) : (
            <div className="space-y-2">
              {data.sections.map((s) => (
                <div key={s.block_id}>
                  <div className="flex justify-between text-sm"><span className="text-gray-700 truncate">{s.label}</span><span className="text-gray-400 shrink-0 ml-2">{s.views}× · {s.avg_seconds}s</span></div>
                  <div className="h-1.5 bg-gray-100 rounded mt-1"><div className="h-1.5 rounded" style={{ width: `${Math.max(4, (s.avg_seconds / maxSec) * 100)}%`, background: TEAL }} /></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-gray-100 bg-white p-5 mt-5">
        <h2 className="font-bold text-gray-900 mb-3">Timeline</h2>
        {(data.timeline || []).length === 0 ? <p className="text-sm text-gray-400">No events yet.</p> : (
          <ul className="space-y-2">
            {data.timeline.map((e) => (
              <li key={e.id} className="flex items-center justify-between text-sm border-b border-gray-50 pb-2">
                <span className="text-gray-700 capitalize">{(e.event_type || "").replace(/_/g, " ")}{e.actor_type ? <span className="text-gray-400"> · {e.actor_type}</span> : null}</span>
                <span className="text-gray-400">{when(e.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ProposalAnalyticsPage;
